// The one way this client talks to parsina.bff.student.
//
// A single fetch wrapper is the whole data layer: attach the Keycloak access
// token, refresh once on a 401 and retry, and surface GraphQL errors WITH their
// `extensions` intact. There is no Apollo Client and no normalized cache — every
// AI field in the schema is a MUTATION precisely because re-issuing it on a
// refetch or a window focus would spend the student's money again.
//
// ── The error vocabulary (core.md §14) ────────────────────────────────────
//
// The BFF's whole language is `extensions.code`, and several codes change what a
// screen DOES rather than what it says:
//
//   UNAUTHENTICATED         → the session is over: raise the session dialog
//                             (core.md §15), never a full-document redirect
//                             from inside a fetch — that unmounts unsaved work
//   NOT_ONBOARDED           → a verified token with no student profile: the boot
//                             shows the onboarding form
//   ENCOUNTER_ALREADY_OPEN  → `extensions.encounterId`: navigate to it instead of
//                             erroring
//   INSUFFICIENT_CREDITS    → the AI refused for lack of پارسه: recover to
//                             «شارژ پارسه», never to a retry
//   INVALID_INPUT           → `extensions.field` names the field
//   SERVICE_UNAVAILABLE     → a downstream is down (and the gate fails closed)
//
// Throwing on `res.status` alone would drop all of them.

import { getToken, isTokenExpired, refreshAccessToken } from './auth.js';
import { BFF_URL } from './runtimeEnv.js';
import { emitSession, SessionEvent } from './session.js';

export const ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  NOT_ONBOARDED: 'NOT_ONBOARDED',
  QUERY_TOO_COMPLEX: 'QUERY_TOO_COMPLEX',
  INVALID_INPUT: 'INVALID_INPUT',
  CASE_NOT_FOUND: 'CASE_NOT_FOUND',
  ENTRY_NOT_FOUND: 'ENTRY_NOT_FOUND',
  ENCOUNTER_NOT_FOUND: 'ENCOUNTER_NOT_FOUND',
  DIAGNOSIS_NOT_FOUND: 'DIAGNOSIS_NOT_FOUND',
  RX_ITEM_NOT_FOUND: 'RX_ITEM_NOT_FOUND',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  THREAD_NOT_FOUND: 'THREAD_NOT_FOUND',
  ENCOUNTER_ALREADY_OPEN: 'ENCOUNTER_ALREADY_OPEN',
  ENCOUNTER_CLOSED: 'ENCOUNTER_CLOSED',
  ANOTHER_ENCOUNTER_OPEN: 'ANOTHER_ENCOUNTER_OPEN',
  CASE_ARCHIVED: 'CASE_ARCHIVED',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  GIFT_NOT_FOUND: 'GIFT_NOT_FOUND',
  GIFT_NOT_USABLE: 'GIFT_NOT_USABLE',
  GIFT_GRANT_FAILED: 'GIFT_GRANT_FAILED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  // Client-side only: fetch itself threw (no network, DNS, CORS).
  NETWORK: 'NETWORK',
});

async function getValidToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    const ok = await refreshAccessToken();
    if (!ok) return null;
    token = getToken();
  }
  return token;
}

async function executeRequest(token, query, variables) {
  return fetch(BFF_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ query, variables }),
  });
}

/** Build the Error a caller sees. Keeps `status`, `extensions` and the full list. */
function buildError(body, status) {
  const gqlError = body?.errors?.[0];
  const err = new Error(gqlError?.message || `BFF error: ${status}`);
  if (status) err.status = status;
  if (gqlError?.extensions) err.extensions = gqlError.extensions;
  if (body?.errors) err.graphQLErrors = body.errors;
  return err;
}

function sessionOver() {
  emitSession(SessionEvent.EXPIRED);
  const err = new Error('Not authenticated');
  err.status = 401;
  err.extensions = { code: ERROR_CODES.UNAUTHENTICATED };
  return err;
}

function networkError(cause) {
  const err = new Error(cause?.message || 'Network error');
  err.extensions = { code: ERROR_CODES.NETWORK };
  err.cause = cause;
  return err;
}

async function bffRequest(query, variables = {}) {
  const token = await getValidToken();
  // No token: the session is OVER, and that is an event the shell handles with a
  // dialog — never a navigation from here, which would unmount unsaved work.
  if (!token) throw sessionOver();

  let res;
  try {
    res = await executeRequest(token, query, variables);
  } catch (cause) {
    throw networkError(cause);
  }

  // A 401 can also mean server-side expiry or clock skew — refresh once and retry.
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw sessionOver();
    try {
      res = await executeRequest(getToken(), query, variables);
    } catch (cause) {
      throw networkError(cause);
    }
    if (res.status === 401) throw sessionOver();
  }

  if (!res.ok) {
    // Apollo sends a normal GraphQL error payload beside a non-2xx status; the
    // code is the only thing that tells 403 NOT_ONBOARDED from 503.
    const body = await res.json().catch(() => null);
    throw buildError(body, res.status);
  }

  const { data, errors } = await res.json();
  // A 200 carrying `errors` is the FIELD-level half. Anything the caller must
  // branch on (CASE_NOT_FOUND, INSUFFICIENT_CREDITS, ENCOUNTER_ALREADY_OPEN)
  // arrives this way, so it has to throw with the code.
  if (errors?.length) throw buildError({ errors });

  return data;
}

/** The GraphQL error code, when the failure carried one. */
export const errorCode = (err) => err?.extensions?.code || null;

/** The field an INVALID_INPUT names, when it names one. */
export const errorField = (err) => err?.extensions?.field || null;

/** Did the AI refuse for lack of پارسه? The recovery is «شارژ پارسه», never a retry. */
export const isOutOfCredits = (err) => errorCode(err) === ERROR_CODES.INSUFFICIENT_CREDITS;

export const isNotOnboarded = (err) => errorCode(err) === ERROR_CODES.NOT_ONBOARDED;

export const isUnauthenticated = (err) =>
  errorCode(err) === ERROR_CODES.UNAUTHENTICATED || err?.status === 401;

/** A downstream could not be reached, or the network is gone. Retry-able. */
export const isUnavailable = (err) =>
  errorCode(err) === ERROR_CODES.SERVICE_UNAVAILABLE
  || errorCode(err) === ERROR_CODES.NETWORK
  || err?.status === 503;

/** The open encounter an ENCOUNTER_ALREADY_OPEN names — the client navigates to it. */
export const alreadyOpenEncounterId = (err) =>
  (errorCode(err) === ERROR_CODES.ENCOUNTER_ALREADY_OPEN ? err?.extensions?.encounterId || null : null);

export const isNotFound = (err) => /_NOT_FOUND$/.test(errorCode(err) || '');

/**
 * The i18n key for an error's headline. The BFF's own message is shown only as
 * secondary detail (core.md §14).
 */
export function errorMessageKey(err) {
  switch (errorCode(err)) {
    case ERROR_CODES.INSUFFICIENT_CREDITS: return 'errors.insufficientCredits';
    case ERROR_CODES.INVALID_INPUT: return 'errors.invalidInput';
    case ERROR_CODES.SERVICE_UNAVAILABLE:
    case ERROR_CODES.NETWORK: return 'errors.unavailable';
    case ERROR_CODES.ENCOUNTER_CLOSED: return 'errors.encounterClosed';
    case ERROR_CODES.ANOTHER_ENCOUNTER_OPEN: return 'errors.anotherEncounterOpen';
    case ERROR_CODES.CASE_ARCHIVED: return 'errors.caseArchived';
    case ERROR_CODES.THREAD_NOT_FOUND: return 'errors.threadNotFound';
    case ERROR_CODES.QUERY_TOO_COMPLEX: return 'errors.queryTooComplex';
    default:
      return isNotFound(err) ? 'errors.notFound' : 'errors.generic';
  }
}

export const bffQuery = bffRequest;
export const bffMutation = bffRequest;
